import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EditSheet } from '@/components/layout/EditSheet';
import { DataTable } from '@/components/tables/DataTable';
import { useAdminAboutContent, useUpdateAboutContent } from '@/lib/admin-queries';
import { AboutContentEditForm } from '@/features/about/AboutContentEditForm';
import { TimelineEditForm } from '@/features/about/TimelineEditForm';
import { AchievementsEditForm } from '@/features/about/AchievementsEditForm';
import { RoadmapEditForm } from '@/features/about/RoadmapEditForm';
import { toast } from '@/components/ui/Toast';

const LABELS: Record<string, string> = {
  story: 'Story',
  mission: 'Mission',
  vision: 'Vision',
  why_we_exist: 'Why we exist',
  core_values: 'Core values',
  timeline: 'Timeline',
  achievements: 'Achievements',
  roadmap: 'Roadmap',
};

export function AboutContentPage() {
  const { data: rows = [], isLoading } = useAdminAboutContent();
  const update = useUpdateAboutContent();
  const [editing, setEditing] = useState<{ key: string; data: Record<string, unknown> } | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Section',
        cell: (r: { key: string }) => <span className="font-medium">{LABELS[r.key] ?? r.key}</span>,
      },
      {
        key: 'key',
        header: 'Key',
        cell: (r: { key: string }) => (
          <code className="rounded bg-bg-subtle px-2 py-0.5 text-xs text-fg-muted">{r.key}</code>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: { key: string; data: Record<string, unknown> }) => (
          <Button variant="secondary" size="sm" onClick={() => setEditing({ key: r.key, data: r.data ?? {} })}>
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  async function onSave(next: Record<string, unknown>) {
    if (!editing) return;
    try {
      await update.mutateAsync({ key: editing.key, data: next });
      toast.success('Section updated');
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">About</p>
        <h1 className="mt-2 display-serif text-display-l text-fg">Page content</h1>
        <p className="mt-2 text-sm text-fg-muted">Edit structured content for /about. JSON-based; the marketing site renders each section from this.</p>
      </header>

      <DataTable
        rows={rows as Array<{ id: string; key: string; data: Record<string, unknown> }>}
        columns={columns as never}
        rowKey={(r) => r.key}
        empty="No about content yet."
      />

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing ? `Edit: ${LABELS[editing.key] ?? editing.key}` : ''}
        size="xl"
      >
        {editing && editing.key === 'timeline' && (
          <TimelineEditForm
            key={editing.key}
            value={editing.data}
            onChange={onSave}
            submitting={update.isPending}
            onCancel={() => setEditing(null)}
          />
        )}
        {editing && editing.key === 'achievements' && (
          <AchievementsEditForm
            key={editing.key}
            value={editing.data}
            onChange={onSave}
            submitting={update.isPending}
            onCancel={() => setEditing(null)}
          />
        )}
        {editing && editing.key === 'roadmap' && (
          <RoadmapEditForm
            key={editing.key}
            value={editing.data}
            onChange={onSave}
            submitting={update.isPending}
            onCancel={() => setEditing(null)}
          />
        )}
        {editing && editing.key !== 'timeline' && editing.key !== 'achievements' && editing.key !== 'roadmap' && (
          <AboutContentEditForm
            key={editing.key}
            sectionKey={editing.key}
            value={editing.data}
            onChange={onSave}
            submitting={update.isPending}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditSheet>
    </div>
  );
}